import drawImageSync from "./drawImage.js";

/**
 * Retrieves the current image dimensions given an enabled element
 *
 * @param {any} image  image.
 * @param {Number} rotation Optional. The rotation angle of the image.
 * @return {{width:Number, height:Number}} The Image dimensions
 * @memberof Internal
 */
function getImageSize(image) {
  return {
    width: image.width,
    height: image.height
  };
}

/**
 * Calculates the horizontal, vertical and minimum scale factor for an image
   @param {{width, height}} windowSize The window size where the image is displayed. This can be any HTML element or structure with a width, height fields (e.g. canvas).
 * @param {any} image  image object
 * @param {Number} rotation Optional. The rotation angle of the image.
 * @return {{horizontalScale, verticalScale, scaleFactor}} The calculated horizontal, vertical and minimum scale factor
 * @memberof Internal
 */
function getImageFitScale(windowSize, image, rotation = null) {
  const imageSize = getImageSize(image, rotation);
  // const rowPixelSpacing = image.rowPixelSpacing || 1;
  // const columnPixelSpacing = image.columnPixelSpacing || 1;
  // const rowPixelSpacing = image.pixelSpacing[0] || 1;
  // const columnPixelSpacing = image.pixelSpacing[1] || 1;
  let rowPixelSpacing = 1; // default rowPixelSpacing
  let columnPixelSpacing = 1; // default colPixelSpacing
  if (image.pixelSpacing === undefined || image.pixelSpacing === null) {
    rowPixelSpacing =
      image.pixelSpacing === undefined || image.pixelSpacing === null
        ? 1
        : image.pixelSpacing[0];
    columnPixelSpacing =
      image.pixelSpacing === undefined || image.pixelSpacing === null
        ? 1
        : image.pixelSpacing[1];
  } else if (image.pixelSpacing.length === 2) {
    rowPixelSpacing =
      image.pixelSpacing[0] === undefined || image.pixelSpacing[0] === null
        ? 1
        : image.pixelSpacing[0];
    columnPixelSpacing =
      image.pixelSpacing[1] === undefined || image.pixelSpacing[1] === null
        ? 1
        : image.pixelSpacing[1];
  } else {
    rowPixelSpacing = 1;
    columnPixelSpacing = 1;
  }

  let verticalRatio = 1;
  let horizontalRatio = 1;

  if (rowPixelSpacing < columnPixelSpacing) {
    horizontalRatio = columnPixelSpacing / rowPixelSpacing;
  } else {
    // even if they are equal we want to calculate this ratio (the ration might be 0.5)
    verticalRatio = rowPixelSpacing / columnPixelSpacing;
  }

  const verticalScale = windowSize.height / imageSize.height / verticalRatio;
  const horizontalScale = windowSize.width / imageSize.width / horizontalRatio;

  // Fit image to window
  return {
    verticalScale,
    horizontalScale,
    scaleFactor: Math.min(horizontalScale, verticalScale)
  };
}

/**
 * Creates a new viewport object containing default values for the image and canvas
 *
 * @param {HTMLElement} canvas A Canvas DOM element
 * @param {Image} image A  Image Object
 * @returns {Viewport} viewport object
 * @memberof Internal
 */
function getDefaultViewport(canvas, image) {
  if (canvas === undefined) {
    throw new Error(
      "getDefaultViewport: parameter canvas must not be undefined"
    );
  }

  // Fit image to window
  const scale = getImageFitScale(canvas, image, 0).scaleFactor;

  let rowPixelSpacing = 1; // default rowPixelSpacing
  let columnPixelSpacing = 1; // default colPixelSpacing
  if (image.pixelSpacing === undefined || image.pixelSpacing === null) {
    rowPixelSpacing =
      image.pixelSpacing === undefined || image.pixelSpacing === null
        ? 1
        : image.pixelSpacing[0];
    columnPixelSpacing =
      image.pixelSpacing === undefined || image.pixelSpacing === null
        ? 1
        : image.pixelSpacing[1];
  } else if (image.pixelSpacing.length === 2) {
    rowPixelSpacing =
      image.pixelSpacing[0] === undefined || image.pixelSpacing[0] === null
        ? 1
        : image.pixelSpacing[0];
    columnPixelSpacing =
      image.pixelSpacing[1] === undefined || image.pixelSpacing[1] === null
        ? 1
        : image.pixelSpacing[1];
  } else {
    rowPixelSpacing = 1;
    columnPixelSpacing = 1;
  }

  return {
    scale,
    translation: {
      x: 0,
      y: 0
    },
    voi: {
      windowWidth: image.windowWidth,
      windowCenter: image.windowCenter
    },
    invert: image.invert,
    pixelReplication: false,
    rotation: 0,
    hflip: false,
    vflip: false,
    modalityLUT: image.modalityLUT,
    voiLUT: image.voiLUT,
    colormap: image.colormap,
    labelmap: Boolean(image.labelmap),
    displayedArea: {
      tlhc: {
        x: 1,
        y: 1
      },
      brhc: {
        x: image.columns,
        y: image.rows
      },
      // rowPixelSpacing: image.pixelSpacing[0] === undefined ? 1 : image.pixelSpacing[0],
      // columnPixelSpacing: image.pixelSpacing[1] === undefined ? 1 : image.pixelSpacing[1],
      rowPixelSpacing: rowPixelSpacing,
      columnPixelSpacing: columnPixelSpacing,

      presentationSizeMode: "NONE"
    }
  };
}

/**
 * @typedef {Object} EnabledElementStub
 * @property {HTMLElement} element  The enabled element
 * @property {HTMLCanvasElement} canvas The current canvas
 * @property {Object} image Currently displayed image
 * @property {Boolean} invalid Whether or not the image pixel data has been changed
 * @property {Boolean} needsRedraw  A flag for triggering a redraw of the canvas without re-retrieving the pixel data, since it remains valid
 * @property {Object} options Layer drawing options
 * @property {Object[]} layers Layers added to the EnabledElement
 * @property {Object} data
 * @property {Object} renderingTools
 * @property {Object} viewport The current viewport
 * @memberof rendering
 */

/**
 * creates a dummy enabled element
 *
 * @param {HTMLCanvasElement} canvas the canvas that will be assigned to the enabled element.
 * @param {any} image An Image loaded by a  Image Loader
 * @param { any } options Options for rendering the image (e.g.enable webgl by {renderer: 'webgl' })
 * @param { any } viewport A set of  viewport parameters
 * @returns {EnabledElementStub} a dummy enabled element
 * @memberof rendering
 */
//function createEnabledElementStub(canvas, image, options, viewport) {
function createEnabledElementStub(canvas, image, uuid, viewport) {
  return {
    element: canvas,
    canvas,
    image,
    invalid: true, // True if image needs to be drawn, false if not
    needsRedraw: true,
    uuid: uuid,
    layers: [],
    data: {},
    renderingTools: {},
    viewport,
    isDataChanging: false
  };
}

/**
 * Render the image to the provided canvas immediately.
 * @param {any} canvas The HTML canvas where the image will be rendered.
 * @param {any} image An Image loaded by a  Image Loader
 * @param {any} [viewport = null] A set of  viewport parameters
 * @param {any} [options = null] Options for rendering the image (e.g. enable webgl by {renderer: 'webgl'})
 * @returns {void}
 * @memberof rendering
 */
//export default function (canvas, image, viewport = null, options = null) {
export default function(canvas, image, viewport = null, uuid = null) {
  if (canvas === undefined) {
    throw new Error("renderToCanvas: parameter canvas cannot be undefined");
  }

  let defaultViewport = null;
  if (viewport === null) {
    defaultViewport = getDefaultViewport(canvas, image);
  } else {
    if (!(viewport.voi === undefined)) {
      if (viewport.voi.windowWidth === undefined) {
        //const defaultViewport = getDefaultViewport(canvas, image);
        defaultViewport = getDefaultViewport(canvas, image);
      } else {
        defaultViewport = viewport;
      }
    }
  }

  const enabledElementStub = createEnabledElementStub(
    canvas,
    image,
    uuid,
    defaultViewport
  );

  drawImageSync(enabledElementStub, enabledElementStub.invalid);

  //return enabledElementStub.viewport;
  return enabledElementStub;
}
